import type { Deal, DealStage } from '@/types'

export type ApiDealStage =
  | 'QUALIFICADO'
  | 'REUNIAO_AGENDADA'
  | 'REUNIAO_REALIZADA'
  | 'PROPOSTA_ELABORACAO'
  | 'PROPOSTA_ENVIADA'
  | 'TESTE_GRATIS'
  | 'RENEGOCIACAO'
  | 'GERAR_CONTRATO'
  | 'AJUSTAR_PROPOSTA'
  | 'CONTRATO_ENVIADO'
  | 'HANDOVER'
  | 'GANHO'
  | 'PERDIDO'
  | 'PAUSADO'
  | 'FECHAMENTO_FUTURO'

const apiToUiStage: Record<ApiDealStage, DealStage> = {
  QUALIFICADO: 'qualificado',
  REUNIAO_AGENDADA: 'reuniao_agendada',
  REUNIAO_REALIZADA: 'reuniao_realizada',
  PROPOSTA_ELABORACAO: 'proposta_elaboracao',
  PROPOSTA_ENVIADA: 'proposta_enviada',
  TESTE_GRATIS: 'teste_gratis',
  RENEGOCIACAO: 'renegociacao',
  GERAR_CONTRATO: 'gerar_contrato',
  AJUSTAR_PROPOSTA: 'ajustar_proposta',
  CONTRATO_ENVIADO: 'contrato_enviado',
  HANDOVER: 'handover',
  GANHO: 'ganho',
  PERDIDO: 'perdido',
  PAUSADO: 'pausado',
  FECHAMENTO_FUTURO: 'fechamento_futuro',
}

const uiToApiStage = Object.fromEntries(
  Object.entries(apiToUiStage).map(([apiStage, uiStage]) => [uiStage, apiStage]),
) as Record<DealStage, ApiDealStage>

export function toUiDealStage(stage: unknown): DealStage {
  if (typeof stage !== 'string') return 'reuniao_agendada'
  if (stage in apiToUiStage) return apiToUiStage[stage as ApiDealStage]
  if (stage in uiToApiStage) return stage as DealStage
  return 'reuniao_agendada'
}

export function toApiDealStage(stage: DealStage): ApiDealStage {
  return uiToApiStage[stage]
}

function readTesteGratisDesde(raw: { etapa?: unknown; stage?: unknown; updatedAt?: unknown; testeGratisDesde?: unknown; dealHistory?: unknown }) {
  if (typeof raw.testeGratisDesde === 'string' && raw.testeGratisDesde) return raw.testeGratisDesde
  const history = Array.isArray(raw.dealHistory) ? raw.dealHistory : []
  const entry = history.find((item) => {
    const next = item && typeof item === 'object' ? (item as { valorNovo?: unknown }).valorNovo : ''
    return String(next).toUpperCase() === 'TESTE_GRATIS'
  }) as { createdAt?: unknown } | undefined
  if (typeof entry?.createdAt === 'string' && entry.createdAt) return entry.createdAt
  if (entry?.createdAt instanceof Date) return entry.createdAt.toISOString()
  if (toUiDealStage(raw.etapa ?? raw.stage) === 'teste_gratis' && typeof raw.updatedAt === 'string') return raw.updatedAt
  return undefined
}

export function stagePatchPayload(stage: DealStage, extra?: Record<string, unknown>) {
  return {
    ...extra,
    etapa: toApiDealStage(stage),
  }
}

export function mapDealFromApi(raw: any): Deal {
  const empresaNome =
    raw.empresaNome ??
    raw.empresa?.nomeFantasia ??
    raw.empresa?.nome ??
    raw.empresa?.razaoSocial ??
    ''

  const closerNome = raw.closerNome ?? raw.owner?.name ?? raw.closer?.name ?? ''

  return {
    ...raw,
    id: raw.id ?? '',
    nome: raw.nome ?? raw.name ?? empresaNome,
    empresaId: raw.empresaId ?? raw.empresa?.id ?? '',
    empresaNome,
    closerId: raw.closerId ?? raw.ownerId ?? raw.owner?.id ?? '',
    closerNome,
    valor: Number(raw.valor ?? 0),
    valorMensal: raw.valorMensal !== undefined && raw.valorMensal !== null ? Number(raw.valorMensal) : undefined,
    valorSetup: raw.valorSetup !== undefined && raw.valorSetup !== null ? Number(raw.valorSetup) : undefined,
    desconto: raw.desconto !== undefined && raw.desconto !== null ? Number(raw.desconto) : undefined,
    probabilidade: Number(raw.probabilidade ?? 0),
    etapa: toUiDealStage(raw.etapa ?? raw.stage),
    stagnant: Boolean(raw.stagnant),
    hasPropostaRascunho: Boolean(raw.hasPropostaRascunho),
    driftDays: Number(raw.driftDays ?? 0),
    testeGratisDesde: readTesteGratisDesde(raw),
    createdAt: raw.createdAt ?? '',
    updatedAt: raw.updatedAt ?? '',
    instanceId: raw.instanceId ?? raw.instance?.id,
    contatoNome: raw.contatoNome ?? raw.instance?.nome,
    email: raw.email ?? raw.instance?.email,
    telefone: raw.telefone ?? raw.instance?.telefone,
    bdrId: raw.bdrId ?? raw.instance?.sdrBdr?.id,
    bdrNome: raw.bdrNome ?? raw.instance?.sdrBdr?.name,
    dataEntradaLead: raw.dataEntradaLead ?? raw.instance?.dataEntrada,
    leadProblema: raw.leadProblema ?? raw.instance?.problema,
    leadOrigem: raw.leadOrigem ?? raw.instance?.origem,
    leadSegmento: raw.leadSegmento ?? raw.instance?.segmento,
    plataforma:
      raw.plataforma ??
      raw.instance?.plataforma ??
      raw.empresa?.plataforma ??
      '',
    signedAt: raw.signedAt ?? undefined,
    contractStartDate: raw.contractStartDate ?? raw.contractStart ?? undefined,
    contractStart: raw.contractStart ?? raw.contractStartDate ?? undefined,
    firstPaymentDate: raw.firstPaymentDate ?? undefined,
    representanteLegal: raw.representanteLegal ?? raw.empresa?.responsavelAssinaturaNome,
    emailFinanceiro: raw.emailFinanceiro ?? raw.empresa?.responsavelFinanceiroEmail,
    reunioes: Array.isArray(raw.reunioes)
      ? raw.reunioes.map((reuniao: Record<string, unknown>) => ({
          id: reuniao.id ? String(reuniao.id) : '',
          data: String(reuniao.data ?? ''),
          tipo: String(reuniao.tipo ?? 'Descoberta'),
          status: String(reuniao.status ?? 'Agendada'),
          notas: reuniao.notas as string | undefined,
          transcricao: reuniao.transcricao as string | undefined,
          closerId: String(reuniao.closerId ?? raw.closerId ?? ''),
          closerNome: (reuniao.closerNome as string | undefined) ?? (reuniao.closer as { name?: string } | undefined)?.name,
        }))
      : undefined,
  }
}

export function mapDealsFromApi(raw: unknown): Deal[] {
  const items = Array.isArray(raw) ? raw : []
  return items.map(mapDealFromApi)
}
